import { useMemo, useState } from 'react';
import useSWRInfinite from 'swr/infinite';
import { useTranslation } from 'react-i18next';
import { PlusIcon } from '@phosphor-icons/react';
import { Combobox, type ComboboxOption } from '@/components/Combobox/Combobox';
import {
    Tooltip,
    TooltipContent,
    TooltipTrigger,
} from '@/components/ui/Tooltip';
import { Thumbnail } from '@/components/Thumbnail';
import { CreateReportModal } from '@/components/CreateReportModal';
import { CACHE_KEYS } from '@/constants/cache-keys';
import { EngineService } from '@/services/engine/engine-service';
import { useCreateReport } from '@/hooks/useCreateReport';
import { revalidateByCacheKeyPrefix } from '@/utils/swr';
import Config from '@/config';
import { I18nNamespace } from '@/lib/i18n-namespaces';
import type { PaginatedResponse } from '@/types/responses';
import type { ReportListItem } from '@/types/report';
import { useEntityActionsMenu } from './EntityActionsMenu';

const PAGE_SIZE = 20;

type ReportsPageKey = readonly [string, string, string, number];

interface AddToReportSubmenuContentProps {
    onSelect: (report: ReportListItem) => void;
    onClose?: () => void;
}

export function AddToReportSubmenuContent({
    onSelect,
    onClose,
}: AddToReportSubmenuContentProps) {
    const { t } = useTranslation(I18nNamespace.EVENTS);
    const menu = useEntityActionsMenu();
    const createReport = useCreateReport();
    const [search, setSearch] = useState('');
    const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);

    const { data, isValidating, size, setSize, mutate } = useSWRInfinite<
        PaginatedResponse<ReportListItem>,
        unknown,
        (
            pageIndex: number,
            previousPageData: PaginatedResponse<ReportListItem> | null,
        ) => ReportsPageKey | null
    >(
        (pageIndex, previousPageData) => {
            if (previousPageData && previousPageData.items.length === 0) {
                return null;
            }
            return [
                CACHE_KEYS.REPORTS_LIST,
                'add-to-report',
                search,
                pageIndex + 1,
            ] as const;
        },
        ([, , currentSearch, page]) =>
            EngineService.getReports({
                page,
                size: PAGE_SIZE,
                search: currentSearch || undefined,
            }),
        { revalidateFirstPage: false, revalidateOnMount: true },
    );

    const reports = useMemo(
        () => data?.flatMap((page) => page.items) ?? [],
        [data],
    );

    const options: ComboboxOption[] = useMemo(
        () =>
            reports.map((report) => ({
                value: report.id,
                label: report.name,
            })),
        [reports],
    );

    const hasMore = useMemo(() => {
        if (!data || data.length === 0) return false;
        const lastPage = data[data.length - 1];
        return lastPage.page.page < lastPage.page.totalPages;
    }, [data]);

    const handleSearch = (query: string) => {
        setSearch(query);
        void setSize(1);
    };

    const handleLoadMore = () => {
        if (isValidating || !hasMore) return;
        void setSize(size + 1);
    };

    const handleToggle = (value: string) => {
        const report = reports.find((current) => current.id === value);
        if (!report) return;
        onSelect(report);
        menu?.close();
        onClose?.();
    };

    const handleOpenCreateModal = () => {
        setIsCreateModalOpen(true);
    };

    const handleCloseCreateModal = () => {
        setIsCreateModalOpen(false);
    };

    const handleCreateSubmit = async (name: string, description?: string) => {
        await createReport({ name, description });
        setSearch('');
        await setSize(1);
        await Promise.all([
            mutate(),
            revalidateByCacheKeyPrefix(CACHE_KEYS.REPORTS_LIST),
        ]);
    };

    return (
        <>
            <Combobox
                options={options}
                selected={[]}
                onToggle={handleToggle}
                onSearch={handleSearch}
                onLoadMore={handleLoadMore}
                hasMore={hasMore}
                isLoading={isValidating}
                autoFocus
                title={t('addToReport.title')}
                titleAction={
                    <Tooltip>
                        <TooltipTrigger asChild>
                            <button
                                type="button"
                                onClick={handleOpenCreateModal}
                                aria-label={t('addToReport.createReport')}
                                className="flex h-6 w-6 items-center justify-center rounded-md text-grey-200 transition-colors hover:bg-grey-550 hover:text-white-100"
                            >
                                <PlusIcon size={16} weight="regular" />
                            </button>
                        </TooltipTrigger>
                        <TooltipContent side="top" sideOffset={8}>
                            {t('addToReport.createReportTooltip')}
                        </TooltipContent>
                    </Tooltip>
                }
                searchPlaceholder={t('addToReport.searchPlaceholder')}
                emptyText={t('addToReport.empty')}
                renderOption={(option) => {
                    const report = reports.find(
                        (current) => current.id === option.value,
                    );
                    if (!report) return null;
                    return <ReportPickerRow report={report} />;
                }}
            />
            <CreateReportModal
                isOpen={isCreateModalOpen}
                onClose={handleCloseCreateModal}
                template={null}
                onSubmit={handleCreateSubmit}
            />
        </>
    );
}

function ReportPickerRow({ report }: { report: ReportListItem }) {
    const { t } = useTranslation(I18nNamespace.EVENTS);
    const firstThumbnail =
        report.previewThumbnails.find((url) => url) ?? undefined;
    const needsAuth = firstThumbnail
        ? firstThumbnail.startsWith(Config.VITE_ENGINE_URL)
        : false;

    return (
        <span className="flex w-full min-w-0 items-center gap-2 py-1.5 px-2">
            <span className="relative size-9 shrink-0 overflow-hidden rounded-md bg-grey-500">
                <Thumbnail
                    size="fill"
                    imageSrc={firstThumbnail}
                    imageUsesAuth={needsAuth}
                    placeholderIcon={<></>}
                    className="rounded-md"
                />
            </span>
            <span className="flex min-w-0 flex-1 flex-col text-left">
                <span className="truncate text-sm text-white-100">
                    {report.name}
                </span>
                <span className="truncate text-xs text-grey-200">
                    {t('addToReport.eventsCount', {
                        count: report.eventCount,
                    })}
                </span>
            </span>
        </span>
    );
}
